import React, {
  ReactNode,
  FC,
  useContext,
  useEffect,
  useState
} from "react";
import { createPortal } from "react-dom";
import { connect } from "react-redux";
import dynamic from "next/dynamic";

import { Context } from "@components/useContext/context";
import { getWidgetSpecificIsVisible } from "@utils/index";
import useRemoveModalBackgroundScroll from "@utils/removeModalBackgroundScroll";
import { ChildrenWrapper, CueText, ModalHeading } from "./styles";

import ModalStickyFooter from "@widgets/modalStickyFooter";

import {
  PrimaryButton,
  SecondaryButton
} from "@acko-tech/building-blocks.ui.button";

import Link from "@components/common/hyperlink";
import { FormatCTAtext } from "@components/mmvNotFoundModal";

const ModalMobile = dynamic(
  () =>
    import("@acko-tech/building-blocks.ui.modal").then((c) => c.ModalMobile),
  { ssr: true }
);

const ModalDesktop = dynamic(
  () =>
    import("@acko-tech/building-blocks.ui.modal").then((c) => c.ModalDesktop),
  { ssr: true }
);

interface Props {
  modalHeading?: string | ReactNode;
  showModal: boolean;
  setShowModal?: Function;
  hideCloseButton?: boolean;
  children: any;
  styles?: any;
  onClose?: Function;
  disableOverlayClose?: boolean;
  modalName?: string;
  bodyMargin?: string;
  wrapperStyles?: any;
  closeButtonStyles?: any;
  modalHeadingStyles?: any;
  cueText?: string;
  isDesktop?: boolean | undefined;
  headerStyles?: any;
  is_visible?: any;
  bodyStyles?: any;
  cueTextStyles?: any;
  dWebCueTextStyles?: any;
  actions: any;
  dwebChildrenStyles?: any;
  showModalFooter?: boolean;
  showPrimaryCta?: boolean;
  showSecondaryCta?: boolean;
  primaryCtaText?: string;
  secondaryCtaText?: string;
  secondaryCtaType?: "LINK" | "BUTTON";
  secondaryCtaLinkType?: "PRIMARY" | "DANGER";
  footerWrapperStyles?: any;
  footerWrapperDStyles?: any;
  secondaryCtaStyles?: any;
  primaryCtaClickHandler?: Function;
  secondaryCtaClickHandler?: Function;
  lockBackgroundInteraction?: boolean;
}

const mapStateToProps = function (state: any, ownProps: any) {
  return {
    is_visible: getWidgetSpecificIsVisible(
      state.configuration?.normalisedConfig,
      ownProps.id
    )
  };
};

const SecondaryCta = ({ ctaType, linkType, text, clickHandler, styles }) => {
  let element: any = <></>;
  if (ctaType === "BUTTON") {
    element = (
      <div style={{ marginTop: "1rem", ...styles }}>
        <SecondaryButton isFullWidth onClick={clickHandler}>
          <FormatCTAtext>{text}</FormatCTAtext>
        </SecondaryButton>
      </div>
    );
  } else if (ctaType === "LINK") {
    element = (
      <Link
        type={linkType || "DANGER"}
        styles={{
          marginTop: "1rem",
          textAlign: "center",
          display: "block",
          width: "100%",
          fontSize: "14px",
          lineHeight: "20px",
          ...styles
        }}
        text={text}
        clickHandler={clickHandler}
        is_visible
      />
    );
  }
  return element;
};

const Modal: FC<Props> = ({
  modalHeading,
  showModal,
  setShowModal = () => { },
  children,
  hideCloseButton = false,
  styles = {},
  onClose,
  disableOverlayClose = false,
  bodyMargin = "",
  wrapperStyles = {},
  closeButtonStyles = {},
  modalHeadingStyles = {},
  cueText,
  headerStyles,
  is_visible,
  bodyStyles,
  actions,
  cueTextStyles,
  dWebCueTextStyles,
  dwebChildrenStyles,
  showModalFooter,
  showPrimaryCta,
  showSecondaryCta,
  footerWrapperStyles,
  footerWrapperDStyles,
  primaryCtaText,
  secondaryCtaText,
  secondaryCtaType,
  secondaryCtaLinkType,
  secondaryCtaStyles,
  primaryCtaClickHandler,
  secondaryCtaClickHandler,
  lockBackgroundInteraction = true
}) => {
  const { isDesktop } = useContext(Context);

  const ModalComponent = isDesktop ? ModalDesktop : ModalMobile;

  const { on_click, on_load, primary_cta_on_click, secondary_cta_on_click } =
    actions || {};

  useEffect(() => {
    if ((showModal || is_visible) && on_load?.length > 0) {
      on_load?.forEach((eachFunc: any) => {
        typeof eachFunc.funCall === "function" &&
          eachFunc.funCall(eachFunc.args);
      });
    }
  }, [on_load, is_visible]);

  const handleHide = () => {
    if (onClose) onClose();
    setShowModal && setShowModal(false);
    if (on_click?.[0] && typeof on_click[0].funCall === "function") {
      on_click[0].funCall(on_click[0].args || {});
    }
  };

  const isOpen = Boolean(showModal || is_visible);
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  // Keep BB modal mounted through show=false so its hide/cleanup effects run
  // before we unmount (avoids racing body-style wipe with scroll locks).
  const [keepMounted, setKeepMounted] = useState(false);

  useEffect(() => {
    let root = document.getElementById("sdui-modal-root");
    if (!root) {
      root = document.createElement("div");
      root.id = "sdui-modal-root";
      document.body.appendChild(root);
    }
    setModalRoot(root);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setKeepMounted(true);
      return;
    }

    if (!keepMounted) return;

    const timeoutId = window.setTimeout(() => {
      setKeepMounted(false);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, keepMounted]);

  useRemoveModalBackgroundScroll(isOpen, lockBackgroundInteraction);

  if (!modalRoot || (!isOpen && !keepMounted)) return null;

  return createPortal(
    <div
      data-sdui-modal-layer
      style={
        lockBackgroundInteraction && isOpen
          ? {
              position: "fixed",
              inset: 0,
              zIndex: 1100,
              pointerEvents: "auto"
            }
          : undefined
      }
      onClick={(event) => {
        if (
          lockBackgroundInteraction &&
          isOpen &&
          event.target === event.currentTarget
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <ModalComponent
        maxWidth="504px"
        maxHeight="85vh"
        urlhash=""
        show={isOpen}
        onHide={handleHide}
        disableOverlayClose={disableOverlayClose}
        showOverlayBackground
        wrapperStyles={{
          padding: "2rem 1.25rem 1.25rem",
          maxHeight: "85vh",
          zIndex: 1000,
          ...wrapperStyles,
          ...(showModalFooter && {
            paddingBottom: "0px"
          })
        }}
        padding={isDesktop ? "2rem" : "1.5rem 1.25rem 1.25rem"}
        bodyMargin={bodyMargin}
        styles={{ overflow: "auto !important", ...styles }}
        hideCloseButton={hideCloseButton}
        hideTopPadding={hideCloseButton}
        bodyStyles={{
          margin: "0px",
          ...bodyStyles
        }}
        closeButtonStyles={{
          top: "0rem",
          ...closeButtonStyles
        }}
        headerStyles={{ ...headerStyles }}
        header={
          <>
            {modalHeading && (
              <ModalHeading
                styles={modalHeadingStyles}
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                {modalHeading}
                {cueText && (
                  <CueText
                    styles={cueTextStyles}
                    dWebStyles={dWebCueTextStyles}
                  >
                    {cueText}
                  </CueText>
                )}
              </ModalHeading>
            )}
          </>
        }
      >
        <ChildrenWrapper
          onClick={(e) => {
            e.stopPropagation();
          }}
          dwebChildrenStyles={dwebChildrenStyles}
        >
          {children}
          {showModalFooter && (
            <ModalStickyFooter
              styles={footerWrapperStyles}
              dStyles={footerWrapperDStyles}
            >
              {showPrimaryCta && (
                <PrimaryButton
                  isFullWidth
                  onClick={() => {
                    if (primary_cta_on_click?.length > 0) {
                      primary_cta_on_click?.forEach((eachFunc: any) => {
                        typeof eachFunc.funCall === "function" &&
                          eachFunc.funCall(
                            //@ts-ignore
                            {
                              ...eachFunc.args
                            } || {}
                          );
                      });
                    }
                    primaryCtaClickHandler?.();
                  }}
                >
                  <FormatCTAtext>{primaryCtaText}</FormatCTAtext>
                </PrimaryButton>
              )}

              {showSecondaryCta && (
                <SecondaryCta
                  ctaType={secondaryCtaType}
                  linkType={secondaryCtaLinkType}
                  text={secondaryCtaText}
                  styles={secondaryCtaStyles}
                  clickHandler={() => {
                    if (secondary_cta_on_click?.length > 0) {
                      secondary_cta_on_click?.forEach((eachFunc: any) => {
                        typeof eachFunc.funCall === "function" &&
                          eachFunc.funCall(
                            //@ts-ignore
                            {
                              ...eachFunc.args
                            } || {}
                          );
                      });
                    }
                    secondaryCtaClickHandler?.();
                  }}
                />
              )}
            </ModalStickyFooter>
          )}
        </ChildrenWrapper>
      </ModalComponent>
    </div>,
    modalRoot
  );
};

export default connect(mapStateToProps)(Modal);
